import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Mic, MonitorUp, PhoneOff, RotateCcw, Users, Video } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import MeetingInspirePanel from 'pages/Meetings/MeetingInspirePanel';
import WiseFriendControlsPanel from 'pages/Meetings/WiseFriendControlsPanel';
import type { WiseFriendControlsLaunchSelection } from 'pages/Meetings/WiseFriendControlsPanel';
import type { AdminInspireDeck } from 'services/api/adminContentClient';
import type {
  MeetingInspireActivity,
  MeetingInspireDeck,
  MeetingInspireResponse,
} from 'services/api/meetingsClient';

type InspireCallPreviewProps = {
  decks: AdminInspireDeck[];
  brandingName: string;
};

const CALL_FRAME_WIDTH = 1180;
const CALL_FRAME_HEIGHT = 664;

const useFittedCallFrame = (activeView: 'presenter' | 'learner') => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const fit = () => {
      const availableWidth = viewport.getBoundingClientRect().width;
      if (availableWidth <= 0) return;
      setScale(Math.min(1, availableWidth / CALL_FRAME_WIDTH));
    };

    fit();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(fit);
      observer.observe(viewport);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [activeView]);

  return {
    viewportRef,
    scale,
    fittedHeight: Math.round(CALL_FRAME_HEIGHT * scale),
  };
};

const toMeetingDecks = (decks: AdminInspireDeck[]): MeetingInspireDeck[] => decks
  .filter((deck) => deck.status !== 'archived')
  .map((deck) => ({
    id: deck.id,
    key: deck.key,
    name: deck.name,
    description: deck.description,
    helper_text: deck.helper_text || '',
    facilitator_notes: deck.facilitator_notes || '',
    accent_color: deck.accent_color,
    cards: [...deck.items]
      .sort((left, right) => left.order - right.order)
      .filter((item) => item.card.status !== 'archived')
      .map(({ card }) => {
        const helperText = card.helper_text?.trim() || '';
        const facilitatorNotes = card.facilitator_notes?.trim() || '';
        return {
          id: card.id,
          key: card.key,
          prompt: card.prompt,
          helper_text: helperText || deck.helper_text || '',
          facilitator_notes: facilitatorNotes || deck.facilitator_notes || '',
          uses_deck_helper_text: !helperText && Boolean(deck.helper_text),
          uses_deck_facilitator_notes: !facilitatorNotes && Boolean(deck.facilitator_notes),
          version: card.version,
          media_url: card.media_url,
        };
      }),
  }));

const VideoTile = ({ name, initials, warm = false }: { name: string; initials: string; warm?: boolean }) => (
  <div className={`relative flex min-h-0 items-center justify-center overflow-hidden rounded-xl border ${warm ? 'border-amber-200/20 bg-[#394354]' : 'border-white/10 bg-[#293241]'}`}>
    <span className={`flex h-16 w-16 items-center justify-center rounded-full text-lg font-bold ${warm ? 'bg-amber-200 text-slate-900' : 'bg-slate-600 text-white'}`}>{initials}</span>
    <span className="absolute bottom-2 left-3 rounded bg-black/45 px-2 py-1 text-[11px] font-medium text-white">{name}</span>
  </div>
);

const VideoGrid = ({ learner }: { learner: boolean }) => (
  <div className="grid h-full grid-cols-2 gap-2 bg-[#151b24] p-3" data-testid="inspire-preview-video-grid">
    <VideoTile name="Jordan · Presenter" initials="J" warm />
    <VideoTile name={learner ? 'You' : 'Alex'} initials={learner ? 'Y' : 'A'} />
    <VideoTile name="Morgan" initials="M" />
    <VideoTile name={learner ? 'Alex' : 'You'} initials={learner ? 'A' : 'Y'} />
  </div>
);

const DailyToolbar = () => (
  <div className="flex h-16 items-center justify-center gap-3 border-t border-white/10 bg-[#10151d] text-white" aria-label="Simulated Daily call controls">
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10"><Mic className="h-4 w-4" /></span>
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10"><Video className="h-4 w-4" /></span>
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10"><MonitorUp className="h-4 w-4" /></span>
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10"><Users className="h-4 w-4" /></span>
    <span className="flex h-10 w-12 items-center justify-center rounded-full bg-red-500"><PhoneOff className="h-4 w-4" /></span>
  </div>
);

function InspireCallPreview({ decks, brandingName }: InspireCallPreviewProps) {
  const [pov, setPov] = useState<'presenter' | 'learner'>('presenter');
  const [activity, setActivity] = useState<MeetingInspireActivity | null>(null);
  const [activities, setActivities] = useState<MeetingInspireActivity[]>([]);
  const [response, setResponse] = useState<MeetingInspireResponse | null>(null);
  const [responsesByActivity, setResponsesByActivity] = useState<Record<string, MeetingInspireResponse[]>>({});
  const [sharedResponse, setSharedResponse] = useState<MeetingInspireResponse | null>(null);
  const [sharedActivity, setSharedActivity] = useState<MeetingInspireActivity | null>(null);
  const [presenterMainOpen, setPresenterMainOpen] = useState(true);
  const { viewportRef, scale, fittedHeight } = useFittedCallFrame(pov);
  const meetingDecks = useMemo(() => toMeetingDecks(decks), [decks]);

  const panelActivity = sharedResponse && sharedActivity ? sharedActivity : activity;
  const presenterResponses = panelActivity ? responsesByActivity[panelActivity.id] || [] : [];

  const launch = (selection: WiseFriendControlsLaunchSelection) => {
    const deck = selection.kind === 'card' ? meetingDecks.find((item) => item.id === selection.deckId) : null;
    const card = selection.kind === 'card' ? deck?.cards.find((item) => item.id === selection.cardId) : null;
    if (selection.kind === 'card' && (!deck || !card)) return;
    setResponse(null);
    setSharedResponse(null);
    setSharedActivity(null);
    setPresenterMainOpen(true);
    const nextActivity: MeetingInspireActivity = {
      id: `preview-${Date.now()}`,
      visibility_mode: selection.visibility,
      status: 'active',
      card_key_snapshot: selection.kind === 'custom' ? 'custom-preview' : card!.key,
      card_prompt_snapshot: selection.kind === 'custom' ? selection.customPrompt : card!.prompt,
      card_helper_text_snapshot: selection.kind === 'custom' ? '' : card!.helper_text,
      facilitator_notes_snapshot: selection.kind === 'custom' ? '' : card!.facilitator_notes,
      card_version_snapshot: selection.kind === 'custom' ? 1 : card!.version,
      deck_name_snapshot: selection.kind === 'custom' ? 'Custom' : deck!.name,
      deck_accent_color_snapshot: selection.kind === 'custom' ? '#F7D58A' : deck!.accent_color,
      program_name_snapshot: brandingName,
      program_logo_url_snapshot: '',
      launched_at: new Date().toISOString(),
      response_count: 0,
    };
    setActivity(nextActivity);
    setActivities((current) => [nextActivity, ...current.filter((item) => item.id !== nextActivity.id)]);
  };

  const saveResponse = (body: string) => {
    const now = new Date().toISOString();
    const saved: MeetingInspireResponse = {
      id: response?.id || 'preview-response',
      display_name: 'Preview learner',
      body,
      created_at: response?.created_at || now,
      updated_at: now,
    };
    setResponse(saved);
    if (activity) {
      const presenterSaved = {
        ...saved,
        display_name: activity.visibility_mode === 'anonymous' ? 'Anonymous' : 'Preview learner',
      };
      setResponsesByActivity((current) => ({ ...current, [activity.id]: [presenterSaved] }));
      setActivities((current) => current.map((item) => item.id === activity.id ? { ...item, response_count: 1 } : item));
      setActivity((current) => current ? { ...current, response_count: 1 } : current);
    }
    return saved;
  };

  const reset = () => {
    setActivity(null);
    setActivities([]);
    setResponse(null);
    setResponsesByActivity({});
    setSharedResponse(null);
    setSharedActivity(null);
    setPresenterMainOpen(true);
    setPov('presenter');
  };

  return (
    <section className="space-y-4" data-testid="inspire-call-preview">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border bg-muted/20 p-4">
        <div>
          <h3 className="text-lg font-semibold">Call preview</h3>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Launch a saved card as the presenter, switch to the learner, submit a response, then switch back to see it arrive. This simulation does not create a Daily room or save meeting responses.</p>
        </div>
        <Button type="button" variant="outline" size="auto" onClick={reset}><RotateCcw className="h-4 w-4" />Reset preview</Button>
      </div>

      <Tabs value={pov} onValueChange={(value) => setPov(value as 'presenter' | 'learner')}>
        <TabsList aria-label="Preview point of view" className="px-1">
          <TabsTrigger value="presenter" className="px-5">Presenter POV</TabsTrigger>
          <TabsTrigger value="learner" className="px-5">Learner POV</TabsTrigger>
        </TabsList>
        <p className="mt-2 text-xs text-muted-foreground">The 1,180 × 664 desktop call frame scales to fit this workspace while preserving its real proportions.</p>

        {(['presenter', 'learner'] as const).map((view) => (
          <TabsContent key={view} value={view} className="mt-3">
            <div className="mx-auto w-full max-w-[1196px] rounded-2xl border border-slate-300 bg-slate-200 p-2 shadow-inner">
              <div
                ref={view === pov ? viewportRef : undefined}
                className="relative w-full overflow-hidden rounded-xl bg-[#151b24]"
                data-testid="inspire-preview-frame-viewport"
                style={{ height: fittedHeight }}
              >
                <div
                  className="absolute left-0 top-0 h-[664px] w-[1180px] overflow-hidden rounded-xl bg-[#151b24] shadow-2xl"
                  data-testid={`inspire-${view}-call-frame`}
                  style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
                >
                <div className="flex h-12 items-center justify-between border-b border-white/10 bg-[#10151d] px-4 text-white">
                  <div><p className="text-xs font-semibold">WiseFriend preview room</p><p className="text-[10px] text-slate-400">Daily-style layout simulator</p></div>
                  <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-[10px] font-semibold text-emerald-200">Preview only</span>
                </div>
                <div className="flex h-[552px] min-h-0">
                  <div className="relative min-w-0 flex-1 overflow-hidden">
                    {panelActivity && (panelActivity.status === 'active' || Boolean(sharedResponse) || (view === 'presenter' && presenterMainOpen)) ? (
                      <MeetingInspirePanel preview={{
                        role: view === 'presenter' ? 'presenter' : 'listener',
                        branding: { name: brandingName },
                        activity: panelActivity,
                        response: view === 'learner' && panelActivity.id === activity?.id ? response : null,
                        responses: view === 'presenter' ? presenterResponses : [],
                        sharedResponse,
                        onSaveResponse: saveResponse,
                        onShareResponse: setSharedResponse,
                        onStopSharing: () => { setSharedResponse(null); setSharedActivity(null); },
                        onClose: () => setPresenterMainOpen(false),
                      }} />
                    ) : <VideoGrid learner={view === 'learner'} />}
                  </div>
                  {view === 'presenter' && (
                    <aside className="relative w-[360px] shrink-0 overflow-hidden border-l border-white/10" aria-label="Presenter WiseFriend controls preview">
                      <WiseFriendControlsPanel preview={{
                        decks: meetingDecks,
                        active: activity?.status === 'active' ? activity : null,
                        latestActivity: activity,
                        activities,
                        responses: activity ? responsesByActivity[activity.id] || [] : [],
                        responsesByActivity,
                        sharedResponse,
                        sharedActivityId: sharedActivity?.id || null,
                        responseCount: activity ? responsesByActivity[activity.id]?.length || 0 : 0,
                        onLaunch: launch,
                        onEnd: () => {
                          setSharedResponse(null);
                          setSharedActivity(null);
                          setPresenterMainOpen(true);
                          if (activity) {
                            const ended: MeetingInspireActivity = { ...activity, status: 'ended', ended_at: new Date().toISOString() };
                            setActivity(ended);
                            setActivities((items) => items.map((item) => item.id === ended.id ? ended : item));
                          }
                        },
                        onShareResponse: (item, selectedActivity) => { setSharedResponse(item); setSharedActivity(selectedActivity); },
                        onStopSharing: () => { setSharedResponse(null); setSharedActivity(null); },
                      }} />
                    </aside>
                  )}
                </div>
                <DailyToolbar />
                </div>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}

export default InspireCallPreview;
